import type { ValidatedLLMResult } from './services/llmResultValidation';
import type { Json } from './types/database.types';

export interface Customer {
  id: string;
  name: string;
  status: 'pending' | 'approved' | 'rejected';
  dateSubmitted: string;
  amountPaid: number;
}

export interface ApartmentComplex {
  id: string;
  name: string;
  address: string;
  totalCustomers: number;
  weeklyCustomers: number;
  weeklyRevenue: number;
  recentActivity: Customer[];
  trend: number[]; // Array of last 7 days counts
}

/**
 * User roles in the RBAC hierarchy (higher = more permissions)
 * SUPER_ADMIN (100) > COMPANY_OWNER (75) > REGIONAL_MANAGER (50) > PROPERTY_MANAGER (25)
 */
export type UserRole = 'SUPER_ADMIN' | 'COMPANY_OWNER' | 'REGIONAL_MANAGER' | 'PROPERTY_MANAGER';

/** Role hierarchy levels for permission checks */
export const ROLE_LEVELS: Record<UserRole, number> = {
  SUPER_ADMIN: 100,
  COMPANY_OWNER: 75,
  REGIONAL_MANAGER: 50,
  PROPERTY_MANAGER: 25,
} as const;

/** Roles that each role can invite */
export const INVITABLE_ROLES: Record<UserRole, UserRole[]> = {
  SUPER_ADMIN: ['COMPANY_OWNER', 'REGIONAL_MANAGER', 'PROPERTY_MANAGER'],
  COMPANY_OWNER: ['REGIONAL_MANAGER', 'PROPERTY_MANAGER'],
  REGIONAL_MANAGER: ['PROPERTY_MANAGER'],
  PROPERTY_MANAGER: [],
} as const;

/** Roles that each role can manage (edit/delete) */
export const MANAGEABLE_ROLES: Record<UserRole, UserRole[]> = {
  SUPER_ADMIN: ['COMPANY_OWNER', 'REGIONAL_MANAGER', 'PROPERTY_MANAGER'],
  COMPANY_OWNER: ['REGIONAL_MANAGER', 'PROPERTY_MANAGER'],
  REGIONAL_MANAGER: ['PROPERTY_MANAGER'],
  PROPERTY_MANAGER: [],
} as const;

export type TimeRange = '7d' | '30d' | '90d' | '1y' | 'all';

export interface User {
  id: string;
  name: string;
  email: string;
  role: UserRole;
  initials: string;
  assignedComplexCount: number;
  assignedComplexIds: string[];
  companyId?: string; // For owners and managers
}

export interface PropertyManagementCompany {
  id: string;
  name: string;
  phoneNumber: string;
  complexes: ApartmentComplex[];
  users: User[];
  /** Company owners are now tracked via users with role=COMPANY_OWNER */
}

export enum ViewState {
  LOGIN = 'LOGIN',
  SUPER_ADMIN_DASHBOARD = 'SUPER_ADMIN_DASHBOARD',
  DASHBOARD = 'DASHBOARD',
  DETAILS = 'DETAILS',
  USER_MANAGEMENT = 'USER_MANAGEMENT',
  USER_DETAILS = 'USER_DETAILS',
  DOCUMENT_UPLOADS = 'DOCUMENT_UPLOADS',
  DOCUMENT_DETAIL = 'DOCUMENT_DETAIL'
}

export interface PlatformData {
  companies: PropertyManagementCompany[];
  superAdmin: User;
}

// ============================================================================
// DATABASE RESPONSE TYPES (for Supabase)
// ============================================================================

/**
 * Raw database response types that come from Supabase
 * These may differ slightly from UI types and need transformation
 */

export interface DatabaseCustomer {
  id: string;
  complex_id: string;
  name: string;
  status: 'pending' | 'approved' | 'rejected';
  date_submitted: string;
  amount_paid: number;
  created_at?: string;
}

export interface DatabaseComplex {
  id: string;
  company_id: string | null;
  name: string;
  address: string | null;
  total_customers?: number;
  created_at: string | null;
  weekly_customers?: number | null;
  weekly_revenue?: number | null;
  monthly_customers?: number | null;
  monthly_revenue?: number | null;
}

export interface DatabaseUser {
  id: string;
  company_id: string | null;
  name: string | null;
  email: string | null;
  role: string | null;
  /** @deprecated Use user_complex_assignments junction table instead */
  assigned_complex_ids?: string[] | null;
  created_at: string | null;
  updated_at: string | null;
  auth_user_id?: string | null;
  first_name?: string | null;
  last_name?: string | null;
  is_active?: boolean | null;
  last_login?: string | null;
  email_confirmed?: boolean;
  email_confirmed_at?: string | null;
}

export interface DatabaseCompany {
  id: string;
  name: string;
  phone_number: string | null;
  created_at: string | null;
  updated_at: string | null;
}

// ============================================================================
// API RESPONSE TYPES
// ============================================================================

export interface ApiResponse<T> {
  data?: T;
  error?: string;
  message?: string;
}

export interface ApiPaginatedResponse<T> {
  data: T[];
  total: number;
  limit: number;
  offset: number;
  hasMore: boolean;
}

// ============================================================================
// FORM/REQUEST TYPES
// ============================================================================

export interface CreateUserRequest {
  name: string;
  email: string;
  role: UserRole;
  company_id?: string;
  /** @deprecated Use user_complex_assignments junction table instead */
  assigned_complex_ids?: string[];
}

export interface UpdateUserPortfolioRequest {
  /** @deprecated Use user_complex_assignments junction table instead */
  assigned_complex_ids?: string[];
}

export interface CreateComplexRequest {
  name: string;
  address: string;
  company_id: string;
}

export interface CreateCompanyRequest {
  name: string;
  owner_name: string;
  owner_email: string;
  phone_number: string;
}

// ============================================================================
// LOADING/ERROR STATES
// ============================================================================

export interface LoadingState {
  isLoading: boolean;
  error: string | null;
  isDone: boolean;
}

export interface DataState<T> {
  data: T | null;
  isLoading: boolean;
  error: string | null;
}

// ============================================================================
// ANONYMOUS DOCUMENT UPLOADS
// ============================================================================

export enum ProcessingStatus {
  PENDING = 'pending',
  PROCESSING = 'processing',
  COMPLETED = 'completed',
  FAILED = 'failed',
}

export enum PaymentStatus {
  PAID = 'paid',
  UNPAID = 'unpaid',
  PENDING = 'pending',
  FAILED = 'failed',
  REFUNDED = 'refunded'
}

/** Confidence level from AI verification - string values from Gemini API, numeric from legacy */
export type LLMConfidenceLevel = 'High' | 'Medium' | 'Low';

export interface LLMResult {
  isValid: boolean;
  confidence: LLMConfidenceLevel | number;
  reasoning: string;
  summary?: string;
  finalScore?: number;
  provider_name?: string;
  license_number?: string;
  red_flags?: string[];
  petName?: string;
  breed?: string;
  petType?: string;
  documentDate?: string;
  expiryDate?: string;
  vaccinations?: string[];
  veterinarianName?: string;
  clinicName?: string;
  extractedText?: string;
  authenticityAnalysis?: {
    overallScore?: number;
    summary?: string;
    breakdown?: Record<string, { score: number; reason: string }>;
    flags?: string[];
  };
  breakdown?: Array<{ category: string; score: number; reason: string }>;
  extractedData: {
    patientName?: string;
    providerName?: string;
    issueDate?: string;
    expirationDate?: string;
    animalType?: string;
    breed?: string;
    weight?: string;
    microchipNumber?: string;
    rabiesVaccineDate?: string;
    additionalNotes?: string;
  };
  microchipNumber?: string;
  rabiesVaccineDate?: string;
  additionalNotes?: string;
}

export interface DocumentUploadFilters {
  paymentStatus?: PaymentStatus;
  processingStatus?: ProcessingStatus;
  // Multi-select support for statuses
  paymentStatuses?: PaymentStatus[];
  processingStatuses?: ProcessingStatus[];
  complexId?: string;
  startDate?: string;
  endDate?: string;
  // Confidence score range (0-100)
  minConfidenceScore?: number;
  maxConfidenceScore?: number;
}

export interface FilterPreset {
  id: string;
  name: string;
  filters: DocumentUploadFilters;
  createdAt: string;
}

// New interfaces to replace 'any'
export interface NormalizedAddressComponent {
  long_name: string;
  short_name: string;
  types: string[];
}

export interface MatchSuggestion {
  property_id: string;
  name: string;
  address: string;
  confidence: number;
}

export interface PreviousClaim {
  id: string;
  status: string;
  date: string;
  property_id?: string;
  manager_notes?: string;
}

export interface UploadMetadata {
  original_filename?: string;
  page_count?: number;
  has_watermark?: boolean;
  ip_address?: string;
  user_agent?: string;
  user_name?: string;
  name?: string;
  email?: string;
  phone?: string;
  [key: string]: string | number | boolean | undefined; // Allow flexibility for other metadata
}

export interface AnonymousUpload {
  id: string;
  name: string;
  email: string;
  phone?: string;
  uploadDate: string;
  uploadType?: string;
  paymentStatus: PaymentStatus;
  paymentAmount: number;
  processingStatus: ProcessingStatus;
  pdfUrl?: string;
  filePath?: string;
  fileName?: string;
  fileSize?: number;
  mimeType?: string;
  llmResult?: ValidatedLLMResult | null;
  // Removed propertyId
  complexId?: string;
  companyId?: string;
  propertyManagementCompany?: string;
  managerNotes?: string;
  assignedAt?: string;
  reviewedBy?: string;
  reviewedAt?: string;
  createdAt: string;
  updatedAt: string;
  rawPropertyName?: string;
  rawPropertyAddress?: string;
  // Removed normalizedAddress fields
  placeId?: string;
  addressValidationStatus?: 'pending' | 'validated' | 'failed' | 'skipped';
  planId?: string;
  matchConfidence?: number;
  matchSuggestions?: MatchSuggestion[];
  claimedAt?: string;
  claimedBy?: string;
  claimStatus?: 'unclaimed' | 'pending_review' | 'claimed' | 'disputed';
  previousClaims?: PreviousClaim[];
  authenticityScore?: number;
}

export interface DatabaseAnonymousUpload {
  id: string;
  user_name: string | null;
  user_email: string | null;
  phone: string | null;
  metadata: Json | null;
  payment_status: string | null;
  payment_amount: number | null;
  processing_status: string | null;
  file_path: string;
  file_name: string;
  file_size: number;
  mime_type: string | null;
  llm_result: Json | null;
  complex_id: string | null;
  property_management_company?: string;
  manager_notes: string | null;
  assigned_at?: string;
  reviewed_by: string | null;
  reviewed_at: string | null;
  created_at: string | null;
  updated_at: string | null;
  raw_property_name: string | null;
  raw_property_address: string | null;
  place_id?: string;
  address_validation_status?: 'pending' | 'validated' | 'failed' | 'skipped';
  claimed_at: string | null;
  claimed_by: string | null;
  claim_status: string | null;
  authenticity_score: number | null;
  stripe_session_id: string | null;
  stripe_payment_intent: string | null;
  upload_type: string | null;
  service_level: string | null;
  stamped_letter_url: string | null;
  upload_id: string | null;
}

export interface UpdateDocumentPropertyRequest {
  complex_id?: string;
  manager_notes?: string;
}

/**
 * Request to claim an unassigned upload to a user's property
 */
export interface ClaimUploadRequest {
  /** The complex ID to assign the upload to */
  complexId: string;
  /** Optional notes about the claim */
  claimNotes?: string;
}

/**
 * Response from claiming an upload
 */
export interface ClaimUploadResponse {
  success: boolean;
  upload: AnonymousUpload;
  message: string;
}

export type SubscriptionStatus = 'active' | 'past_due' | 'canceled' | 'incomplete' | 'trialing';

export interface BillingState {
  status: SubscriptionStatus;
  currentPeriodEnd: string;
  planId: string;
}

// ============================================================================
// ROLE-BASED DOCUMENT ACCESS CONTROL
// ============================================================================

/**
 * Access level for a document
 * - full: User can see all fields including LLM results, score, and document
 * - limited: User can only see basic info (name, email, property, status)
 * - none: User cannot see this upload at all
 */
export type DocumentAccessLevel = 'full' | 'limited' | 'none';

/**
 * Limited view of an upload for unassigned documents
 * Used when user doesn't have full access to the document
 */
export interface LimitedUpload {
  id: string;
  name: string;
  email: string;
  propertyName?: string;
  propertyCity?: string;
  propertyState?: string;
  processingStatus: ProcessingStatus;
  paymentStatus: PaymentStatus;
  uploadDate: string;
  accessLevel: 'limited';
}

/**
 * Full upload with access level indicator
 */
export interface FullAccessUpload extends AnonymousUpload {
  accessLevel: 'full';
}

/**
 * Union type for uploads with access control
 */
export type AccessControlledUpload = FullAccessUpload | LimitedUpload;

/**
 * Response from access-controlled upload query
 */
export interface AccessControlledUploadsResponse {
  uploads: AccessControlledUpload[];
  total: number;
  fullAccessCount: number;
  limitedAccessCount: number;
}

/**
 * Database response from get_uploads_with_access_level RPC function
 */
export interface DatabaseUploadWithAccessLevel {
  upload_id: string;
  access_level: DocumentAccessLevel;
  user_name: string | null;
  user_email: string | null;
  phone: string | null;
  file_path: string | null;
  file_name: string | null;
  file_size: number | null;
  processing_status: string;
  payment_status: string;
  authenticity_score: number | null;
  llm_result: LLMResult | null;
  metadata: UploadMetadata | null;
  manager_notes: string | null;
  complex_id: string | null;
  created_at: string;
  updated_at: string | null;
  limited_property_name: string | null;
  limited_property_city: string | null;
  limited_property_state: string | null;
  company_id: string | null;
  company_name: string | null;
}

/**
 * Database response from get_upload_counts_by_access_level RPC function
 */

export interface DatabaseAccessLevelCounts {
  total_count: number;
  full_access_count: number;
  limited_access_count: number;
}

export interface ComplexFilters {
  uploadCount?: 'none' | 'low' | 'medium' | 'high';
  revenueRange?: '0' | '1-500' | '501-2000' | '2001+';
  status?: 'active' | 'inactive' | 'new';
  assignedTo?: string; // 'unassigned' or user ID
}

// ============================================================================
// COMPLEX FILTER PRESETS (Phase 3)
// ============================================================================

/**
 * Saved filter preset for the SuperAdmin Complex filtering UI
 */
export interface ComplexFilterPreset {
  id: string;
  userId: string;
  name: string;
  filters: ComplexFilters;
  isDefault: boolean;
  createdAt: string;
  updatedAt: string;
}

/**
 * Database representation of a filter preset
 */
export interface DatabaseComplexFilterPreset {
  id: string;
  user_id: string;
  name: string;
  filters: ComplexFilters;
  is_default: boolean;
  created_at: string;
  updated_at: string;
}

/**
 * Request to create a new filter preset
 */
export interface CreateFilterPresetRequest {
  name: string;
  filters: ComplexFilters;
  isDefault?: boolean;
}

/**
 * Request to update an existing filter preset
 */
export interface UpdateFilterPresetRequest {
  name?: string;
  filters?: ComplexFilters;
  isDefault?: boolean;
}
// ============================================================================
// RBAC PERMISSION HELPERS (Phase 5)
// ============================================================================

/**
 * Check if a role can manage (edit/delete) another role
 */
export function canManageRole(managerRole: UserRole, targetRole: UserRole): boolean {
  return MANAGEABLE_ROLES[managerRole]?.includes(targetRole) ?? false;
}

/**
 * Check if a role can invite another role
 */
export function canInviteRole(inviterRole: UserRole, targetRole: UserRole): boolean {
  return INVITABLE_ROLES[inviterRole]?.includes(targetRole) ?? false;
}

/**
 * Get the numeric level for a role (higher = more permissions)
 */
export function getRoleLevel(role: UserRole): number {
  return ROLE_LEVELS[role] ?? 0;
}

/**
 * Check if one role has higher permissions than another
 */
export function isHigherRole(role1: UserRole, role2: UserRole): boolean {
  return getRoleLevel(role1) > getRoleLevel(role2);
}

/**
 * Permission check result with reason
 */
export interface PermissionCheckResult {
  allowed: boolean;
  reason?: string;
}

/**
 * Check if a user can manage another user
 * @param manager The user attempting the action
 * @param target The user being managed
 * @returns Permission check result with reason if denied
 */
export function canManageUser(manager: User, target: User): PermissionCheckResult {
  // SUPER_ADMIN can manage anyone
  if (manager.role === 'SUPER_ADMIN') {
    return { allowed: true };
  }

  // Must be in same company
  if (manager.companyId !== target.companyId) {
    return { allowed: false, reason: 'Cannot manage users in other companies' };
  }

  // Check role hierarchy
  if (!canManageRole(manager.role, target.role)) {
    return { allowed: false, reason: `${manager.role} cannot manage ${target.role}` };
  }

  return { allowed: true };
}

/**
 * Check if a user can create properties
 */
export function canCreateProperty(user: User): boolean {
  return ['SUPER_ADMIN', 'COMPANY_OWNER', 'REGIONAL_MANAGER'].includes(user.role);
}

/**
 * Check if a user can edit a property
 * @param user The user attempting to edit
 * @param propertyId The property being edited
 */
export function canEditProperty(user: User, propertyId: string): boolean {
  if (user.role === 'SUPER_ADMIN') return true;
  if (user.role === 'COMPANY_OWNER') return true; // Can edit any in their company
  if (user.role === 'REGIONAL_MANAGER') {
    return user.assignedComplexIds.includes(propertyId);
  }
  return false; // PROPERTY_MANAGER cannot edit
}

/**
 * Check if a user can view all companies
 */
export function canViewAllCompanies(user: User): boolean {
  return user.role === 'SUPER_ADMIN';
}

/**
 * Check if a user can edit their company
 */
export function canEditCompany(user: User): boolean {
  return ['SUPER_ADMIN', 'COMPANY_OWNER'].includes(user.role);
}

/**
 * Get display name for a role
 */
export function getRoleDisplayName(role: UserRole): string {
  const displayNames: Record<UserRole, string> = {
    SUPER_ADMIN: 'Super Admin',
    COMPANY_OWNER: 'Company Owner',
    REGIONAL_MANAGER: 'Regional Manager',
    PROPERTY_MANAGER: 'Property Manager',
  };
  return displayNames[role] ?? role;
}
